/**
 * WizardNavigation Component
 * Provides navigation buttons for the contract wizard
 */

import React from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Save as SaveIcon,
  Send as SubmitIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  PlayArrow as GenerateIcon,
} from '@mui/icons-material';
import { useContractFormContext } from './ContractFormContext';
import {
  getTerminalBalance,
  isTerminalBalanceZero,
  SCHEDULE_TERMINAL_BALANCE_TOLERANCE,
} from '../../../utils/scheduleValidation';
import { formatNumber } from '../../../utils/formatters';

function WizardNavigation({
  activeStep,
  totalSteps = 5,
  showSummaryStep = false,
  nextDisabled = false,
  onBack,
  onNext,
  onSaveDraft,
  onSubmitForApproval,
  onCancel,
  onEditContract,
  onDownloadPDF,
  onGenerateSchedule,
  isGeneratingPDF = false,
}) {
  const {
    isEdit,
    hasSchedule,
    submitting,
    generatingSchedule,
    initialData,
    summaryData,
  } = useContractFormContext();

  const isFirstStep = activeStep === 0;
  const isSummaryStep = showSummaryStep && activeStep === 5;
  const isReviewStep = activeStep === 4;
  const isLoading = submitting || generatingSchedule;

  // Schedule must terminate at (approximately) zero before submission. This
  // mirrors the API guard in POST /contracts/:id/submit-for-approval.
  const scheduleForBalanceCheck = summaryData?.schedule;
  const terminalBalance = getTerminalBalance(scheduleForBalanceCheck);
  const terminalBalanceOk =
    !Array.isArray(scheduleForBalanceCheck) || scheduleForBalanceCheck.length === 0
      ? true // empty schedule is already blocked by hasSchedule
      : isTerminalBalanceZero(scheduleForBalanceCheck);

  const canSubmitForApproval = (isReviewStep || isSummaryStep) && isEdit && hasSchedule && terminalBalanceOk;

  const submitTooltip = !isEdit
    ? 'Save & close first, then generate schedule from the contract detail page'
    : !hasSchedule
      ? 'Generate amortization schedule first'
      : !terminalBalanceOk
        ? `Schedule final balance is ${formatNumber(terminalBalance, 2)} — must be within ${SCHEDULE_TERMINAL_BALANCE_TOLERANCE.toFixed(2)} of zero. Regenerate the schedule.`
        : 'Submit contract for approval';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        py: 2,
        mb: 3,
        borderTop: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
        Cancel
      </Button>

      {!isFirstStep && (
        <Button variant="outlined" startIcon={<BackIcon />} onClick={onBack} disabled={isLoading}>
          Back
        </Button>
      )}

      {(!isEdit || initialData?.status === 'Draft') && (
        <Button
          variant="outlined"
          startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={onSaveDraft}
          disabled={isLoading}
        >
          Save &amp; Close
        </Button>
      )}

      {/* Summary step: Edit Contract Details (Draft only) + Download PDF */}
      {isSummaryStep && onEditContract && (!isEdit || initialData?.status === 'Draft') && (
        <Button variant="contained" startIcon={<EditIcon />} onClick={onEditContract}>
          Edit Contract Details
        </Button>
      )}

      {isSummaryStep && onDownloadPDF && (
        <Button
          variant="outlined"
          startIcon={isGeneratingPDF ? <CircularProgress size={20} /> : <PdfIcon />}
          onClick={onDownloadPDF}
          disabled={isGeneratingPDF}
        >
          {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
        </Button>
      )}

      {/* Non-review/summary steps: Next button.
          NOTE: intentionally NOT disabled on nextDisabled. A disabled button
          swallows clicks, so an incomplete step gave the user no feedback about
          WHY they were stuck. Instead the button stays clickable and handleNext
          validates on click — firing the floating toast listing the exact
          blockers (e.g. a rolled-funds/sources mismatch) when the step is
          invalid, and only advancing when it's valid. The tooltip is a passive
          hint; the toast is the real answer. Only isLoading truly disables it. */}
      {!isReviewStep && !isSummaryStep && (
        <Tooltip title={nextDisabled ? 'Complete the required fields on this step to continue' : ''}>
          <span>
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={onNext}
              disabled={isLoading}
            >
              Next
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Review step only: Generate/Regenerate Schedule (new contracts and Draft edits). Hidden on Summary view. */}
      {isReviewStep && (!isEdit || initialData?.status === 'Draft') && onGenerateSchedule && (
        <Button
          variant="contained"
          color="secondary"
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <GenerateIcon />}
          onClick={onGenerateSchedule}
          disabled={isLoading}
        >
          {hasSchedule ? 'Regenerate Schedule' : 'Generate Schedule'}
        </Button>
      )}

      {/* Review step: View Summary before Submit */}
      {isReviewStep && showSummaryStep && (
        <Button variant="outlined" endIcon={<NextIcon />} onClick={onNext} disabled={isLoading}>
          View Summary
        </Button>
      )}

      {/* Review/Summary steps: Submit for Approval (Draft only) */}
      {(isReviewStep || isSummaryStep) && (!isEdit || initialData?.status === 'Draft') && (
        <Tooltip title={submitTooltip}>
          <span>
            <Button
              variant="contained"
              color="primary"
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SubmitIcon />}
              onClick={onSubmitForApproval}
              disabled={isLoading || !canSubmitForApproval}
            >
              Submit for Approval
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}

export default WizardNavigation;
